import {
  type ReactElement,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  DEFAULT_AGENT_COMPOSER_SESSION_TITLE,
  buildAgentComposerSessionKey,
} from '~/stores/workspace/agentComposerSessionTypes';
import {
  patchAgentComposerSessionByKey,
  resetAgentComposerSessionTitleByKey,
} from '~/stores/workspace/agentComposerSessionActions';
import { useWorkspaceStore } from '~/stores/workspace/workspaceStore';

export { DEFAULT_AGENT_COMPOSER_SESSION_TITLE_I18N_KEY as DEFAULT_AGENT_SESSION_TITLE_I18N_KEY } from '~/stores/workspace/agentComposerSessionTypes';

export type AgentSessionHeaderHandlers = {
  sessionId?: string;
  sessionKey?: string | null;
  workspaceId?: string;
  onNewChat: () => void;
  onTitleChange: (title: string) => void;
};

type AgentSessionHeaderContextValue = {
  handlers: AgentSessionHeaderHandlers | null;
  setHandlers: (value: AgentSessionHeaderHandlers | null) => void;
};

const AgentSessionHeaderContext =
  createContext<AgentSessionHeaderContextValue | null>(null);

export const AgentSessionHeaderProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => {
  const [handlers, setHandlers] = useState<AgentSessionHeaderHandlers | null>(
    null
  );

  const value = useMemo(
    () => ({
      handlers,
      setHandlers,
    }),
    [handlers]
  );

  return (
    <AgentSessionHeaderContext.Provider value={value}>
      {children}
    </AgentSessionHeaderContext.Provider>
  );
};

const resolveHeaderSessionKey = (
  handlers: AgentSessionHeaderHandlers | null
): string | null => {
  if (handlers?.sessionKey) {
    return handlers.sessionKey;
  }
  if (!handlers?.workspaceId) return null;
  return buildAgentComposerSessionKey(handlers.workspaceId, handlers.sessionId);
};

export const useAgentSessionHeader = (): {
  sessionTitle: string;
  setSessionTitle: (title: string) => void;
  resetSessionTitle: () => void;
  handlers: AgentSessionHeaderHandlers | null;
  setHandlers: (value: AgentSessionHeaderHandlers | null) => void;
} => {
  const value = useContext(AgentSessionHeaderContext);
  if (!value) {
    throw new Error('AgentSessionHeaderProvider is missing');
  }

  const sessionKey = resolveHeaderSessionKey(value.handlers);
  const sessionTitle = useWorkspaceStore(state => {
    if (!sessionKey) return DEFAULT_AGENT_COMPOSER_SESSION_TITLE;
    return (
      state.agentComposerByKey[sessionKey]?.sessionTitle ??
      DEFAULT_AGENT_COMPOSER_SESSION_TITLE
    );
  });

  const setSessionTitle = useCallback(
    (title: string): void => {
      if (!sessionKey) return;
      const trimmed = title.trim();
      patchAgentComposerSessionByKey(sessionKey, {
        sessionTitle: trimmed || DEFAULT_AGENT_COMPOSER_SESSION_TITLE,
      });
    },
    [sessionKey]
  );

  const resetSessionTitle = useCallback((): void => {
    resetAgentComposerSessionTitleByKey(sessionKey);
  }, [sessionKey]);

  return {
    sessionTitle,
    setSessionTitle,
    resetSessionTitle,
    handlers: value.handlers,
    setHandlers: value.setHandlers,
  };
};
